import React, { useState } from "react";
import * as dmsService from "../../../services/dmsService";
import * as finService from "../../../services/finService";
import * as inventoryService from "../../../services/inventoryService";
import useFinOptions from "../../Fin/useFinOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { DataTable, act, hint, linkBtn } from "../../Fin/gl/glUi";
import { AppDropdown, EmptyState, Field, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, formatDate, inputStyle, pageStyle, useLoad } from "../../../components/ui";

// Counter Sales: parts and accessories sold over the counter and paid at once (stock out, tax
// invoice, payment - in one go). Cash needs the cashier's drawer open (Cash & Bank → Cash drawers).
function NewSale({ onHide, onDone }) {
  const fin = useFinOptions(["locations", "receipt-methods", "tax-rates"]);
  const items = useLoad(() => inventoryService.getItems({ is_active: 1 }), []);
  const customers = useLoad(() => finService.listCustomers({ size: 200 }), []);
  const [h, setH] = useState({ location_id: null, customer_account_id: null, walkin_name: "", walkin_phone: "", method_id: null, reference: "" });
  const [lines, setLines] = useState([{ item_id: null, quantity: 1, unit_price: "", discount_amount: "", tax_rate_code: "AE_STD" }]);
  const [busy, setBusy] = useState(false);
  const itemRows = items.data || [];
  const itemOptions = (Array.isArray(itemRows) ? itemRows : []).map((i) => ({ label: `${i.sku} – ${i.name} (${Number(i.current_qty)} in stock)`, value: i.id, row: i }));
  const set = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const priceOf = (l) => (l.unit_price !== "" ? Number(l.unit_price) : Number(((itemOptions.find((o) => o.value === l.item_id) || {}).row || {}).selling_price || 0));
  const total = lines.reduce((s, l) => s + Math.max(0, priceOf(l) * Number(l.quantity || 0) - Number(l.discount_amount || 0)) * (l.tax_rate_code ? 1.05 : 1), 0);
  const loc = h.location_id || (fin.locations && fin.locations[0] ? fin.locations[0].value : null);
  const save = async () => {
    setBusy(true);
    const res = await act(() =>
      dmsService.postCounterSale({
        location_id: loc,
        customer_account_id: h.customer_account_id || undefined,
        customer: h.customer_account_id ? undefined : { name: h.walkin_name, phone: h.walkin_phone },
        lines: lines
          .filter((l) => l.item_id)
          .map((l) => ({
            item_id: l.item_id,
            quantity: Number(l.quantity),
            unit_price: l.unit_price === "" ? undefined : Number(l.unit_price),
            discount_amount: Number(l.discount_amount || 0),
            tax_rate_code: l.tax_rate_code || null,
            // serial-tracked items (batteries ...): the serials sold; batches go earliest expiry first
            details: l.serials ? String(l.serials).split(/[\s,]+/).filter(Boolean).map((serial_no) => ({ serial_no })) : undefined,
          })),
        payment: h.method_id ? { method_id: h.method_id, reference: h.reference } : undefined,
      })
    );
    setBusy(false);
    if (res) {
      onDone(res.data);
      onHide();
    }
  };
  return (
    <SlidePanel
      show
      onHide={() => !busy && onHide()}
      title="New counter sale"
      width={940}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={busy}>
            Post sale
          </PrimaryButton>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0 12px" }}>
        <Field label="Location">
          <AppDropdown id="cs-loc" value={loc} options={fin.locations || []} onChange={(e) => setH({ ...h, location_id: e.value })} />
        </Field>
        <Field label="Customer (blank: walk-in)">
          <AppDropdown id="cs-cust" value={h.customer_account_id} filter options={[{ label: "Walk-in customer", value: null }, ...(customers.data || []).map((c) => ({ label: `${c.name} (${c.account_number})`, value: c.id }))]} onChange={(e) => setH({ ...h, customer_account_id: e.value })} />
        </Field>
        {!h.customer_account_id ? (
          <>
            <Field label="Walk-in name" required>
              <input className="form-control" style={inputStyle} value={h.walkin_name} onChange={(e) => setH({ ...h, walkin_name: e.target.value })} />
            </Field>
            <Field label="Phone" required>
              <input className="form-control" style={inputStyle} value={h.walkin_phone} onChange={(e) => setH({ ...h, walkin_phone: e.target.value })} />
            </Field>
          </>
        ) : null}
      </div>
      {lines.map((l, i) => (
        <div key={i} className="d-flex flex-wrap gap-2 align-items-center mb-2">
          <div style={{ flex: 1, minWidth: 260 }}>
            <AppDropdown id={`cs-it-${i}`} value={l.item_id} filter options={itemOptions} onChange={(e) => set(i, { item_id: e.value })} placeholder="Item" />
          </div>
          <input type="number" min={1} className="form-control" style={{ ...inputStyle, width: 80 }} aria-label="Quantity" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
          <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 120 }} aria-label="Price" placeholder={fmtMoney(priceOf(l))} value={l.unit_price} onChange={(e) => set(i, { unit_price: e.target.value })} />
          <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 110 }} aria-label="Discount" placeholder="Discount" value={l.discount_amount} onChange={(e) => set(i, { discount_amount: e.target.value })} />
          <input className="form-control" style={{ ...inputStyle, width: 150 }} aria-label="Serial numbers" placeholder="Serial no(s)" value={l.serials || ""} onChange={(e) => set(i, { serials: e.target.value })} />
          <div style={{ width: 150 }}>
            <AppDropdown id={`cs-tx-${i}`} value={l.tax_rate_code} options={(fin["tax-rates"] || []).map((o) => ({ label: o.label, value: o.code }))} onChange={(e) => set(i, { tax_rate_code: e.value })} />
          </div>
          {lines.length > 1 ? (
            <button type="button" style={linkBtn} onClick={() => setLines(lines.filter((_, j) => j !== i))}>
              Remove
            </button>
          ) : null}
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={() => setLines([...lines, { item_id: null, quantity: 1, unit_price: "", discount_amount: "", tax_rate_code: "AE_STD" }])}>
        Add item
      </button>
      <div className="d-flex flex-wrap gap-2 align-items-end">
        <Field label="Paid by" required style={{ width: 240 }}>
          <AppDropdown id="cs-pay" value={h.method_id} options={(fin["receipt-methods"] || []).filter((m) => ["CASH", "CARD", "BANK_TRANSFER", "ONLINE"].includes(m.row.method_type))} onChange={(e) => setH({ ...h, method_id: e.value })} />
        </Field>
        <Field label="Reference (card authorisation)" style={{ width: 220 }}>
          <input className="form-control" style={inputStyle} value={h.reference} onChange={(e) => setH({ ...h, reference: e.target.value })} />
        </Field>
        <div style={{ marginLeft: "auto", fontSize: 18, fontWeight: 700, marginBottom: 16 }}>About {fmtMoney(total)}</div>
      </div>
      <div style={hint}>The exact total, with VAT per line, is on the tax invoice.</div>
    </SlidePanel>
  );
}

export default function CounterSales() {
  const { data, loading, error, reload } = useLoad(() => dmsService.listCounterSales({}), []);
  const [creating, setCreating] = useState(false);
  return (
    <div style={pageStyle}>
      <PageHeader title="Counter Sales" subtitle="Parts and accessories sold over the counter" actions={<PrimaryButton onClick={() => setCreating(true)}>New sale</PrimaryButton>} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          empty={<EmptyState title="No counter sales" />}
          columns={[
            { label: "Sale", render: (r) => <b>{r.sale_number}</b> },
            { label: "Date", render: (r) => formatDate(r.sale_date) },
            { label: "Customer", render: (r) => r.customer_name },
            { label: "Total", num: true, render: (r) => `${fmtMoney(r.total)} ${r.currency}` },
            { label: "", render: (r) => (r.ar_trx_id ? <button type="button" style={linkBtn} onClick={() => finService.transactionPdf(r.ar_trx_id, `${r.sale_number}.pdf`)}>Tax invoice</button> : null) },
          ]}
        />
      </LoadState>
      {creating ? (
        <NewSale
          onHide={() => setCreating(false)}
          onDone={(d) => {
            reload();
            if (d && d.ar_trx_id && window.confirm("Print the tax invoice?")) finService.transactionPdf(d.ar_trx_id, `${d.sale_number}.pdf`);
          }}
        />
      ) : null}
    </div>
  );
}
